import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { get } from 'lodash'
import TetraLogo from '@assets/icons/tetraMainLogo.svg?react'

import config from '@src/config'
import { Address } from '@components/form'
import Modal from '@components/ui/Modal'
import {
  CallUsBody,
  CallUsFooter,
  RedirectFooter,
} from '@components/miscellaneous/CallUs'
import getURLParams from '@utils/getURLParams'
import urlPathSetter from '@utils/urlPathSetter'
import validateEmpRefCode from '@utils/validateEmpRefCode'
import isValidUsPhone from '@utils/isValidUsPhone'
import { useFormContext } from '@context/FormContext'
import PhoneNumber from './PhoneNumber'

/**
 * Adds the shared US phone rule to a yup string schema. Three fields need it — the primary phone,
 * the HVAC additional contact and the HEA utility-bill account holder — so it lives in one place
 * rather than being copy-pasted per branch, where the copies could drift apart.
 */
const withUsPhoneTest = (schema) =>
  schema.test('validate', 'Please enter a valid US phone number', async (value) =>
    value === undefined ? false : isValidUsPhone(value)
  )

// Inline field wrapper used by all native inputs
const Field = ({ label, required, children, error, show = true }) => {
  if (!show) return null
  return (
    <div>
      <label className="form-label">
        {label}
        {required && <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error && <div className="error-message">{error}</div>}
    </div>
  )
}

// Radio choices for "Whose name is on your … utility bills?" (HEA only)
const nameOnBillOptions = [
  { value: 'mine', label: 'My Name' },
  { value: 'someoneElse', label: 'Someone Else' },
]

/**
 * One row of the help-text lists. The glyph sits in a fixed-width column so ✅, ✔️ and ❌ — which
 * have different widths — all produce the same left text edge, and wrapped lines hang under the
 * text rather than sliding back under the glyph. Mirrors the checklist in steps/Success/Success.jsx.
 */
const HelpListItem = ({ glyph, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-5 text-center" aria-hidden="true">{glyph}</span>
    <span>{children}</span>
  </li>
)

/**
 * Customer education for the utility-bill name question. Shown in a Modal so
 * customers understand why the name has to match the utility account exactly.
 * max-w-prose caps the measure at 65ch — the Modal body is 720px wide at
 * size="large", which is far too long a line for comfortable reading.
 */
const UtilityBillNameHelpBody = () => (
  <div className="text-left w-full max-w-prose text-base text-stone-600 leading-relaxed">
    <p className="mb-4">
      To approve your Home Energy Assessment, we must verify your eligibility directly with your gas
      and electric utility companies.
    </p>
    <p className="mb-4">
      The account holder name(s) you provide{' '}
      <strong className="text-stone-700">must match the name(s) on your utility account</strong>.
      Even small differences—such as using a nickname, leaving off a joint account holder, or
      entering a spouse&apos;s name when they aren&apos;t listed on the account—can prevent us from
      verifying your information.
    </p>
    <p className="mb-2 font-medium text-stone-700">
      Providing the correct account holder names helps us:
    </p>
    <ul className="mb-4 space-y-2">
      <HelpListItem glyph="✅">Verify your eligibility quickly</HelpListItem>
      <HelpListItem glyph="✅">Avoid delays in scheduling or approving your appointment</HelpListItem>
      <HelpListItem glyph="✅">Ensure you receive all available program benefits</HelpListItem>
      <HelpListItem glyph="✅">
        Prevent additional follow-up calls, emails, or texts requesting missing information
      </HelpListItem>
    </ul>
    <p className="mb-2 font-semibold text-stone-700">Examples:</p>
    <ul className="mb-4 space-y-2">
      <HelpListItem glyph="✔️">Robert Smith &amp; Jennifer Smith</HelpListItem>
      <HelpListItem glyph="✔️">Robert A. Smith</HelpListItem>
      <HelpListItem glyph="❌">Bob Smith</HelpListItem>
      <HelpListItem glyph="❌">Jennifer Smith (if only Robert is listed on the account)</HelpListItem>
    </ul>
    <p className="mb-0">
      <strong className="text-stone-700">Not sure whose name is on the account?</strong> You can find
      it on your utility bill or by logging into your gas or electric utility&apos;s online account.
    </p>
  </div>
)

const ContactInfo = ({
  currentStepIndex,
  setValidationSchema,
}) => {
  const { values, errors, submitCount, setFieldValue } = useFormContext()

  if (!values) return null

  values.step = 'contactInfo'
  urlPathSetter(values)
  const urlParams = new URLSearchParams(document.location.search)
  const [addressValid, setAddressValid] = useState(false)
  const [isMaAddress, setIsMaAddress] = useState(true)
  const [isWellesleyTown, setIsWellesleyTown] = useState(false)
  const [isEmpRef, setIsEmpRef] = useState(false)
  const [validZip, setValidZip] = useState(true)
  const [sourceValues, setSourceValues] = useState([])

  const options = {
    source:
      values?.path === 'hvac' && values?.salesOrService !== 'service'
        ? [
            { label: 'Direct Mail', value: 'Direct Mail' },
            { label: 'Email', value: 'Email' },
            { label: 'Web Search', value: 'Web Search' },
            { label: 'Social Media', value: 'Social Media' },
            { label: 'Home Energy Assessment', value: 'Home Energy Assessment' },
            { label: 'HomeAdvisor', value: 'HomeAdvisor' },
            { label: 'Leadify', value: 'Leadify' },
            { label: 'Customer Referral', value: 'Customer Referral' },
            { label: 'Employee Referral', value: 'Employee Referral' },
            { label: 'Partners', value: 'Partners' },
            { label: 'Repeat Customer', value: 'Repeat Customer' },
          ]
        : [
            { label: 'CAP', value: 'CAP' },
            { label: 'Direct Mail', value: 'Direct Mail' },
            { label: 'Web Search', value: 'Web' },
            { label: 'Social Media', value: 'Social' },
            { label: 'TV', value: 'TV' },
            { label: 'Radio', value: 'Radio' },
            { label: 'Event', value: 'Outreach Program' },
            { label: 'Partner', value: 'Partners' },
            { label: 'Employee Referral', value: 'Employee Referral' },
            { label: 'Word of Mouth', value: 'Word of mouth' },
          ],
    languagePref: [
      { label: 'English', value: 'English' },
      { label: 'Spanish', value: 'Spanish' },
      { label: 'Portuguese', value: 'Portuguese' },
      { label: 'Arabic', value: 'Arabic' },
      { label: 'Vietnamese', value: 'Vietnamese' },
      { label: 'Haitian Creole', value: 'Haitian Creole' },
      { label: 'Chinese(Cantonese)', value: 'Chinese(Cantonese)' },
      { label: 'Chinese(Mandarin)', value: 'Chinese(Mandarin)' },
      { label: 'Other', value: 'Other' },
    ],
  }

  const setSourceDropDown = () => {
    const paramsObj = getURLParams()
    const empRef = paramsObj['empRef'] === 'true'
    setIsEmpRef(empRef)
    if (empRef) {
      setSourceValues([
        { label: 'Employee Referral', value: 'Employee Referral' },
      ])
      return
    }
    setSourceValues(options.source)
  }

  const setInitialValuesFromParams = () => {
    const fnameParam = urlParams.get('fname')
    const lnameParam = urlParams.get('lname')

    if (fnameParam && !values.contactInfo?.firstName) {
      setFieldValue('contactInfo.firstName', fnameParam)
    }

    if (lnameParam && !values.contactInfo?.lastName) {
      setFieldValue('contactInfo.lastName', lnameParam)
    }
    setFieldValue('contactInfo.languagePref', 'English')
  }

  useEffect(() => {
    setSourceDropDown()
    setInitialValuesFromParams()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEmpRef) {
      setFieldValue('contactInfo.source', 'Employee Referral')
    }
  }, [isEmpRef, setFieldValue])

  useEffect(() => {
    if (!isWellesleyTown) {
      setFieldValue('contactInfo.dataSharingAuthorized', false)
    }
  }, [isWellesleyTown, setFieldValue])

  // secondaryFirstName/secondaryLastName/secondaryPhoneNumber have a different owner on each path:
  //   hvac        → additional contact, gated by the secondaryContact checkbox
  //   hea         → utility-bill account holder, gated by nameOnBill
  //   healthyhome → none are collected, so nothing may persist
  // Only HVAC renders the checkbox, so a stale `true` on any other path is reset rather than
  // trusted — otherwise it gates validation on hidden fields and reaches the lead payload.
  useEffect(() => {
    const path = values?.path
    const { secondaryContact, nameOnBill } = values?.contactInfo || {}

    // The three fields are always collected together, so they are always cleared together.
    const clearSecondary = () => {
      setFieldValue('contactInfo.secondaryFirstName', undefined)
      setFieldValue('contactInfo.secondaryLastName', undefined)
      setFieldValue('contactInfo.secondaryPhoneNumber', undefined)
    }

    if (path === 'hvac') {
      // The checkbox owns these fields here — only clear them when it is unchecked.
      if (!secondaryContact) clearSecondary()
      return
    }

    // Keep checkbox-only state from leaking into the payload if the user switched paths
    if (secondaryContact) {
      setFieldValue('contactInfo.secondaryContact', false)
    }

    // HEA reuses all three fields for the utility-bill account holder.
    if (path !== 'hea' || nameOnBill !== 'someoneElse') {
      clearSecondary()
    }
  }, [
    values?.path,
    values?.contactInfo?.nameOnBill,
    values?.contactInfo?.secondaryContact,
    setFieldValue,
  ])

  useEffect(() => {
    const source = values.contactInfo?.source
    if (source === 'Employee Referral') {
      setIsEmpRef(true)
    } else {
      setIsEmpRef(false)
    }
  }, [values.contactInfo?.source])

  useEffect(() => {
    // `path` and `howHeated` live at the form root, so they cannot be referenced as siblings from
    // inside the nested contactInfo shape — branch on the closure instead (as `address` does with
    // addressValid) and keep them in this effect's dep array.
    const isHeaPath = values?.path === 'hea'
    const billNamesRequired = isHeaPath && values?.contactInfo?.nameOnBill === 'someoneElse'
    const billUtilityLabel = values?.howHeated === 'Gas' ? 'gas' : 'electric'

    setValidationSchema(
      yup.object().shape({
        contactInfo: yup
          .object()
          .shape({
            firstName: yup.string().required('This field is required'),
            lastName: yup.string().required('This field is required'),
            email: yup
              .string()
              .test(
                'validate',
                'Please enter a valid Email',
                async (value) => {
                  if (value === undefined) return false
                  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
                }
              )
              .required('This field is required')
              .email('Please enter a valid email address'),
            phone: withUsPhoneTest(yup.string()).required('This field is required'),
            // HEA: whose name is on the utility bill. Required on that path only.
            nameOnBill: isHeaPath
              ? yup
                  .string()
                  .oneOf(nameOnBillOptions.map(({ value }) => value), 'Please select an option')
                  .required('Please select an option')
              : yup.string().notRequired(),
            // On HEA these two carry the utility-bill account holder and are gated by nameOnBill.
            // On every other path they remain the additional-contact name, gated by secondaryContact.
            secondaryFirstName: isHeaPath
              ? billNamesRequired
                ? yup
                    .string()
                    .trim()
                    .required(`Please enter the first name on the ${billUtilityLabel} utility bill`)
                : yup.string().notRequired()
              : yup.string().when(['secondaryContact'], {
                  is: (secondaryContact) => secondaryContact,
                  then: (schema) =>
                    schema.trim().required('Please enter Secondary Firstname'),
                  otherwise: (schema) => schema.notRequired(),
                }),
            secondaryLastName: isHeaPath
              ? billNamesRequired
                ? yup
                    .string()
                    .trim()
                    .required(`Please enter the last name on the ${billUtilityLabel} utility bill`)
                : yup.string().notRequired()
              : yup.string().when(['secondaryContact'], {
                  is: (secondaryContact) => secondaryContact,
                  then: (schema) =>
                    schema.trim().required('Please enter Secondary Lastname'),
                  otherwise: (schema) => schema.notRequired(),
                }),
            // On HEA this is the account holder's phone, required alongside their name so the
            // energy advisor can reach whoever actually owns the utility account.
            secondaryPhoneNumber: isHeaPath
              ? billNamesRequired
                ? withUsPhoneTest(yup.string()).required('This field is required')
                : yup.string().notRequired()
              : yup.string().when(['secondaryContact'], {
                  is: (secondaryContact) => secondaryContact,
                  then: (schema) =>
                    withUsPhoneTest(schema).required('This field is required'),
                  otherwise: (schema) => schema.notRequired(),
                }),
            address: yup
              .string()
              .required('This field is required')
              .test(
                'testing',
                'Please select a valid address from the dropdown',
                async () => {
                  return addressValid
                }
              ),
            languagePref: yup
              .string()
              .oneOf(options.languagePref.map(({ value }) => value))
              .required('This field is required'),
            source: yup
              .string()
              .oneOf((sourceValues.length > 0 ? sourceValues : options.source).map(({ value }) => value))
              .required('This field is required')
              .test('labelChange', '', function (value) {
                value === 'Employee Referral'
                  ? setIsEmpRef(true)
                  : setIsEmpRef(false)
                return true
              }),
            sourceRef: yup.string().when('source', {
              is: (source) => source === 'Employee Referral',
              then: (schema) =>
                schema
                  .required('Field is required')
                  .test('validate', 'Invalid Referral Code', async (value) => {
                    if (value === undefined) return true
                    return value.length < 4 ? false : validateEmpRefCode(value)
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
            details: yup.string(),
            timeline: yup.string(),
            addressComponents: yup.object(),
          })
          .required(),
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStepIndex,
    addressValid,
    sourceValues,
    values?.contactInfo?.secondaryContact,
    values?.contactInfo?.nameOnBill,
    values?.path,
    values?.howHeated,
    setValidationSchema,
  ])

  const activeSourceList = sourceValues.length > 0 ? sourceValues : options.source
  const showSecondary = !!values?.contactInfo?.secondaryContact
  // HEA asks for the utility-bill account holder; HVAC keeps the additional-contact checkbox
  const isHeaPath = values?.path === 'hea'
  const showBillNames = isHeaPath && values?.contactInfo?.nameOnBill === 'someoneElse'
  const billUtility = values?.howHeated === 'Gas' ? 'Gas' : 'Electric'
  const showEmpRef = isEmpRef
  const showHvacFields = values?.path === 'hvac' && values?.salesOrService !== 'service'
  const showWellesley = isWellesleyTown

  const bannerText = values?.path === 'hea'
    ? 'Great news — your home qualifies for a free Mass Save® Home Energy Assessment!'
    : values?.path === 'hvac'
    ? 'Great news — you\'re eligible for our free HVAC consultation!'
    : 'Great news — your home qualifies for our free services!'

  return (
    <div className="max-w-2xl mx-auto px-4">

      {/* Qualifying banner */}
      {values?.path !== 'healthyhome' && (
        <div
          className="flex items-center gap-3 rounded-xl px-5 py-3.5 mb-6 text-sm font-semibold"
          style={{ background: '#f0f8e0', color: '#4a6e18', border: '1.5px solid #c8e68a', animation: 'fadeUp 0.4s ease both' }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
            <circle cx="9" cy="9" r="9" fill="#99c543" />
            <path d="M5 9l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{bannerText}</span>
        </div>
      )}

      {/* Section A: About You */}
      <div className="bg-white rounded-2xl p-6 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}>
        <h2 className="font-semibold text-stone-900 text-base mb-5 flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white flex-shrink-0"
            style={{ background: '#4675ad' }}
          >
            A
          </span>
          About You
        </h2>

        {/* First / Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Field
            label="First Name"
            required
            error={submitCount > 0 ? errors?.contactInfo?.firstName : undefined}
          >
            <input
              type="text"
              autoComplete="given-name"
              className="form-field"
              placeholder="Jane"
              value={values?.contactInfo?.firstName ?? ''}
              onChange={e => setFieldValue('contactInfo.firstName', e.target.value)}
            />
          </Field>
          <Field
            label="Last Name"
            required
            error={submitCount > 0 ? errors?.contactInfo?.lastName : undefined}
          >
            <input
              type="text"
              autoComplete="family-name"
              className="form-field"
              placeholder="Smith"
              value={values?.contactInfo?.lastName ?? ''}
              onChange={e => setFieldValue('contactInfo.lastName', e.target.value)}
            />
          </Field>
        </div>

        {/* Email / Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Field
            label="Email Address"
            required
            error={submitCount > 0 ? errors?.contactInfo?.email : undefined}
          >
            <input
              type="email"
              autoComplete="email"
              className="form-field"
              placeholder="jane@example.com"
              value={values?.contactInfo?.email ?? ''}
              onChange={e => setFieldValue('contactInfo.email', e.target.value)}
            />
          </Field>
          <PhoneNumber field="phone" title="Phone Number" />
        </div>

        {/* Language Preference */}
        <div className="mb-5">
          <label className="form-label">
            Language Preference
          </label>
          <div className="relative">
            <select
              className="form-field form-select"
              value={values?.contactInfo?.languagePref ?? 'English'}
              onChange={e => setFieldValue('contactInfo.languagePref', e.target.value)}
            >
              <option value="">Select language…</option>
              {options.languagePref.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {submitCount > 0 && errors?.contactInfo?.languagePref && (
            <div className="error-message">{errors.contactInfo.languagePref}</div>
          )}
        </div>

        {/* Utility-bill account holder — HEA only. Replaces the additional-contact checkbox so we
            can verify eligibility against the exact name on the gas/electric account. */}
        {isHeaPath && (
          <div className="mb-3">
            <label className="form-label">
              Whose name is on your electric and natural gas (if applicable) utility bills?
              <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>
            </label>

            <div className="mb-3 -mt-1">
              <Modal
                title="Why we need this information"
                size="large"
                trigger={(toggleModal) => (
                  <button
                    type="button"
                    className="notice-link"
                    style={{ fontSize: 13 }}
                    onClick={() => toggleModal()}
                  >
                    Why we need this information
                  </button>
                )}
                body={() => <UtilityBillNameHelpBody />}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {nameOnBillOptions.map(opt => (
                <label className="check-row" key={opt.value}>
                  <input
                    type="radio"
                    name="nameOnBill"
                    value={opt.value}
                    checked={values?.contactInfo?.nameOnBill === opt.value}
                    onChange={() => setFieldValue('contactInfo.nameOnBill', opt.value)}
                  />
                  <span className="check-row-label">
                    <strong>{opt.label}</strong>
                  </span>
                </label>
              ))}
            </div>

            {submitCount > 0 && errors?.contactInfo?.nameOnBill && (
              <div className="error-message">{errors.contactInfo.nameOnBill}</div>
            )}
          </div>
        )}

        {/* Name on the utility account — shown when the bill is in someone else's name */}
        {showBillNames && (
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-200 mb-1">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">
              Name on {billUtility} Utility Bill
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <Field
                label="First Name on Bill"
                required
                error={submitCount > 0 ? errors?.contactInfo?.secondaryFirstName : undefined}
              >
                <input
                  type="text"
                  className="form-field"
                  placeholder="Robert"
                  value={values?.contactInfo?.secondaryFirstName ?? ''}
                  onChange={e => setFieldValue('contactInfo.secondaryFirstName', e.target.value)}
                />
              </Field>
              <Field
                label="Last Name on Bill"
                required
                error={submitCount > 0 ? errors?.contactInfo?.secondaryLastName : undefined}
              >
                <input
                  type="text"
                  className="form-field"
                  placeholder="Smith"
                  value={values?.contactInfo?.secondaryLastName ?? ''}
                  onChange={e => setFieldValue('contactInfo.secondaryLastName', e.target.value)}
                />
              </Field>
            </div>
            <PhoneNumber field="secondaryPhoneNumber" title="Phone Number on Bill" />
          </div>
        )}

        {/* Secondary contact toggle — HVAC only (HEA asks the utility-bill question above) */}
        {values?.path === 'hvac' && (
        <label className="check-row mb-3">
          <input
            type="checkbox"
            checked={!!values?.contactInfo?.secondaryContact}
            onChange={e => setFieldValue('contactInfo.secondaryContact', e.target.checked)}
          />
          <span className="check-row-label">
            <strong>Is there an additional person we can include?</strong><br />
            <span style={{ color: '#78716c' }}>Ex: Spouse, Landlord, Child, Parent, Renter, Roommate</span>
          </span>
        </label>
        )}

        {/* Secondary contact fields */}
        {showSecondary && values?.path === 'hvac' && (
          <div className="bg-stone-50 rounded-xl p-4 border border-stone-200 mb-1">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-3">
              Additional Contact
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <Field
                label="First Name"
                error={submitCount > 0 ? errors?.contactInfo?.secondaryFirstName : undefined}
              >
                <input
                  type="text"
                  className="form-field"
                  placeholder="John"
                  value={values?.contactInfo?.secondaryFirstName ?? ''}
                  onChange={e => setFieldValue('contactInfo.secondaryFirstName', e.target.value)}
                />
              </Field>
              <Field
                label="Last Name"
                error={submitCount > 0 ? errors?.contactInfo?.secondaryLastName : undefined}
              >
                <input
                  type="text"
                  className="form-field"
                  placeholder="Smith"
                  value={values?.contactInfo?.secondaryLastName ?? ''}
                  onChange={e => setFieldValue('contactInfo.secondaryLastName', e.target.value)}
                />
              </Field>
            </div>
            <PhoneNumber field="secondaryPhoneNumber" title="Phone Number" />
          </div>
        )}
      </div>

      {/* SMS Consent */}
      <div className="mb-4">
        <label className="check-row sms-row">
          <input
            type="checkbox"
            checked={!!values?.contactInfo?.textOutIn}
            onChange={e => setFieldValue('contactInfo.textOutIn', e.target.checked)}
          />
          <span className="check-row-label">
            I agree to be contacted by HomeWorks Energy by text, email or call, to arrange or perform energy efficiency or HVAC work.{' '}
            <em>Text STOP to unsubscribe.</em>
            <a
              href="https://homeworksenergy.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4675ad', display: 'block', marginTop: 4, fontSize: 12 }}
            >
              View Privacy Policy
            </a>
          </span>
        </label>
      </div>

      {/* Section B: Your Home */}
      <div className="bg-white rounded-2xl p-6 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)' }}>
        <h2 className="font-semibold text-stone-900 text-base mb-5 flex items-center gap-2">
          <span
            className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white flex-shrink-0"
            style={{ background: '#99c543' }}
          >
            B
          </span>
          Your Home
        </h2>

        {/* Address */}
        <div className="mb-4">
          <Address
            label="Home Address *"
            name="contactInfo.address"
            alt="contactInfo.addressComponents"
            placeholder="Start typing your Massachusetts address…"
            setAddressValid={setAddressValid}
            setIsMaAddress={setIsMaAddress}
            setIsWellesleyTown={setIsWellesleyTown}
            setValidZip={setValidZip}
          />
          <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5" stroke="#a8a29e" />
              <path d="M6 5v3M6 4h.01" stroke="#a8a29e" strokeLinecap="round" />
            </svg>
            Massachusetts addresses only. Choose from the dropdown for best results.
          </p>
        </div>

        {/* How did you hear */}
        <div className="mb-4">
          <label className="form-label">
            How did you hear about us?
            <span style={{ color: '#dc3545', marginLeft: 2 }}>*</span>
          </label>
          <div className="relative">
            <select
              className="form-field form-select"
              value={values?.contactInfo?.source ?? ''}
              onChange={e => setFieldValue('contactInfo.source', e.target.value)}
            >
              <option value="">Select one…</option>
              {activeSourceList.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {submitCount > 0 && errors?.contactInfo?.source && (
            <div className="error-message">{errors.contactInfo.source}</div>
          )}
        </div>

        {/* Referral code */}
        <div className={showEmpRef ? '' : 'mb-0'}>
          <Field
            label={showEmpRef ? 'Employee Referral Code' : 'Add referral code (optional)'}
            required={showEmpRef}
            error={submitCount > 0 ? errors?.contactInfo?.sourceRef : undefined}
          >
            <input
              type="text"
              className="form-field"
              placeholder={showEmpRef ? 'Enter referral code' : 'Optional'}
              value={values?.contactInfo?.sourceRef ?? ''}
              onChange={e => setFieldValue('contactInfo.sourceRef', e.target.value)}
            />
          </Field>
        </div>

        {/* HVAC-only fields */}
        {showHvacFields && (
          <>
            <div style={{ height: 1, background: '#e7e5e4', margin: '16px 0' }} />
            <div className="mb-3">
              <Field
                label="Brief project details"
                error={submitCount > 0 ? errors?.contactInfo?.details : undefined}
              >
                <input
                  type="text"
                  className="form-field"
                  placeholder="Describe your project briefly…"
                  value={values?.contactInfo?.details ?? ''}
                  onChange={e => setFieldValue('contactInfo.details', e.target.value)}
                />
              </Field>
            </div>
            <Field
              label="Project timeline"
              error={submitCount > 0 ? errors?.contactInfo?.timeline : undefined}
            >
              <input
                type="text"
                className="form-field"
                placeholder="e.g., Within 3 months, ASAP, etc."
                value={values?.contactInfo?.timeline ?? ''}
                onChange={e => setFieldValue('contactInfo.timeline', e.target.value)}
              />
            </Field>
          </>
        )}
      </div>

      {/* Wellesley data sharing (conditional) */}
      {showWellesley && (
        <div className="mb-4">
          <label className="check-row">
            <input
              type="checkbox"
              checked={!!values?.contactInfo?.dataSharingAuthorized}
              onChange={e => setFieldValue('contactInfo.dataSharingAuthorized', e.target.checked)}
            />
            <span className="check-row-label">
              Please share my name and email with Town of Wellesley sustainability staff so that they can inform me about incentives and no-cost resources to assist me on my home energy journey. I understand that my personal information will never be shared externally.
            </span>
          </label>
        </div>
      )}

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
        <div className="trust-badge">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1L2 3v4c0 2.2 1.7 4.2 4 4.8C8.3 11.2 10 9.2 10 7V3L6 1z" fill="#99c543" />
          </svg>
          Secure
        </div>
        <div className="trust-badge">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 3C4 3 1.5 7 1.5 7S4 11 7 11s5.5-4 5.5-4S10 3 7 3z" stroke="#4675ad" strokeWidth="1.2" />
            <circle cx="7" cy="7" r="1.8" fill="#4675ad" />
          </svg>
          Private
        </div>
        <div className="trust-badge">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="#f2733b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          No Spam
        </div>
        <div className="trust-badge">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="1" y="1" width="10" height="10" rx="2" stroke="#78716c" strokeWidth="1.2" />
            <path d="M4 6h4M4 4h4M4 8h2" stroke="#78716c" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          Never Sold
        </div>
      </div>

      {/* Non-MA address modal */}
      {!isMaAddress && (
        <Modal
          initialOpen
          title="Sorry"
          body={() => (
            <CallUsBody
              message="This is not a Massachusetts address. Please choose a Massachusetts address or call a HomeWorks Energy Specialist at (781) 305-3319. Thank you!"
            />
          )}
          footer={(toggleModal) => <CallUsFooter toggleModal={toggleModal} />}
        />
      )}

      {/* Out-of-zip HVAC modal */}
      {!validZip && values?.path === 'hvac' && (
        <Modal
          initialOpen
          title="To Better Serve Your Neighborhood!"
          body={() => (
            <CallUsBody
              message="Your home is outside the HomeWorks service territory but we can serve you through our partner, Tetra, which offers virtual visits throughout Massachusetts. Click below to schedule your free virtual visit."
              DisplayLogo={TetraLogo}
            />
          )}
          footer={(toggleModal) => (
            <RedirectFooter
              redirectUrl={config.tetraUrl}
              toggleModal={toggleModal}
              buttonTitle="Book Free Visit"
            />
          )}
        />
      )}
    </div>
  )
}

ContactInfo.info = {
  title: 'Contact Info',
  key: 'contactInfo',
}

ContactInfo.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setValidationSchema: PropTypes.func.isRequired,
  setEnableSubmitButton: PropTypes.func,
}

export default ContactInfo
